import { useCallback, useEffect, useState } from 'react';
import { GalleryImage } from './partials/Galleries/ThumbnailGallery';
import { useBrowserPushEventId, useFetchUrl } from '../../_lib/ViewFetcher';
import { ImageGallery } from '../../types/api/ImageType';
import { useGet } from '../../hooks/useGet';
import { EditableImage } from './partials/Galleries/EditableGallery/EditableGallery';
import { useWebSockets } from '../../_lib/WebSocketManager';

function updateImageProp<T extends GalleryImage>(oldImage: T, newImage: ImageGallery) {
  return { ...oldImage, image: newImage };
}

export function useSyncImages(initialState: EditableImage[], onImageUpdate: (image: EditableImage) => void) {
  const fetchUrl = useFetchUrl();
  const { response, get } = useGet<{ images: EditableImage[] }>();

  const [syncedImages, setSyncedImages] = useState(initialState);
  const subscribe = useWebSockets();
  useEffect(() => {
    setSyncedImages(response?.data ? response.data.images : initialState);
  }, [initialState, response]);

  const refetchImages = useCallback(() => get(fetchUrl), [get, fetchUrl]);

  const handleUpdate = useCallback(
    (editableImage: EditableImage) =>
      setTimeout(() => {
        onImageUpdate(editableImage);
        const { image } = editableImage;
        setSyncedImages(prev => {
          return prev.map(img => (img.image.imageId === image.imageId ? updateImageProp(img, image) : img));
        });
      }, 1000),
    [onImageUpdate]
  );

  const browserPushEventId = useBrowserPushEventId();

  useEffect(() => subscribe(`${browserPushEventId}:CREATE`, setSyncedImages), [browserPushEventId, subscribe]);
  useEffect(
    () => subscribe(`${browserPushEventId}:UPDATE`, handleUpdate),
    [browserPushEventId, handleUpdate, subscribe]
  );
  return { syncedImages, setSyncedImages, refetchImages };
}
